/* eslint-disable react/no-unused-state */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import nsToken from '@netsapiens/netsapiens-js/dist/token';
import nsApi from '@netsapiens/netsapiens-js/dist/api';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { defineMessages, FormattedMessage } from 'react-intl';

import * as actions from '../../../state';
import { changeBoard } from '../../../events';

import BroNoConfirm from './BroNoConfirm';
import NoChart from './NoChart';
import HeaderItem from './HeaderItem';
import CardBase from './card/CardBase';
import styles from './styles.module.css';
import { readStateUser, selectLayout, selectSocket } from '../../../state';

const messages = defineMessages({
  BOARD_UPDATED: {
    id: 'BOARD_UPDATED',
    defaultMessage: 'Board updated from changes made by another user.',
  },
});

const ResponsiveReactGridLayout = WidthProvider(Responsive);

// let pollInterval;
// let firstScale;
let viewModeToggleTimestamp;

/* getExpandedStyle may apply down-scaling to a selected card if it
 * doesn't fit on the screen with the sidesheet open. when the scaling
 * is applied as a css transform, it causes the chart SVG to draw to
 * an incorrect height/width. applyScalingComplex will attempt to
 * apply up-scaling to the chart SVG so that it inflates into the
 * appropriate space */
// const applyScalingComplex = (chartId, scale) => {
//   const timeout = 5000; // stop polling if no match after 5 seconds
//   const timerStart = Date.now();
//
//   const chartEl = document.getElementById(`chart_${chartId}`);
//   const cardBody = chartEl.childNodes[1];
//
//   /* apply scaling to the card using css transform:scale. scaling is
//      * appended to existing transforms because the react-grid-layout
//      * plugin uses transform:position to place cards  */
//   if (!chartEl.style.transform.includes('scale')) { // dont scale if already scaled
//     chartEl.style.transform = `${chartEl.style.transform} scale(${scale})`;
//   }
//
//   /* nudge cardBody to force the browser to redraw it */
//   cardBody.style.width = `${chartEl.clientWidth - 1}px`;
//
//   /* if pollInterval is already in use, clear it */
//   if (pollInterval) {
//     clearInterval(pollInterval);
//   }
//
//   pollInterval = setInterval(() => {
//     const loopChartEl = document.getElementById(`chart_${chartId}`); // re-get element to see changes
//     const loopCardBody = loopChartEl.childNodes[1];
//
//     if (!loopCardBody.className.includes('mui-loader')) { // wait for loader to dismiss
//       setTimeout(() => { // push these style applications into the next cycle
//         loopCardBody.style.width = '100%'; // nudge cardBody back to the correct width
//
//         const chartBody = loopCardBody.childNodes[0];
//
//         if (chartBody) { // sometimes this isn't found. race condition?
//           chartBody.style.transformOrigin = 'top left';
//
//           /* scale to apply to the chartbody is the inverse of the scale applied to the card */
//           let scaleFactor = 1 / scale;
//
//           /* the first scaling application seems to play by different rules than
//            * all subsequent applications. firstScale logic only needs to trigger
//            * once per component mounting. */
//           if (firstScale) {
//             scaleFactor = 1;
//             firstScale = false;
//           }
//
//           chartBody.style.transform = `scale(${scaleFactor})`;
//         }
//       }, 1);
//       clearInterval(pollInterval);
//     }
//
//     /* if something goes wrong, the interval will self destruct */
//     if (Date.now() - timerStart > timeout) {
//       clearInterval(pollInterval);
//     }
//   }, 25);
// };

function Dashboard() {
  const dispatch = useDispatch();

  const board = useSelector(actions.readStateBoard);
  const charts = useSelector(actions.readStateCharts);
  const layout = useSelector(selectLayout);
  const renderRequired = useSelector((state) => state?.board?.renderRequired);
  const selectedChartId = useSelector((state) => state?.board?.selectedChartId);
  const socket = useSelector(selectSocket);
  const user = useSelector(readStateUser);

  const [value, setValue] = useState(0);

  useEffect(() => {
    // firstScale = true;

    function refreshBoard() {
      setValue(value + 1);
    }

    window.addEventListener('resize', refreshBoard);

    return () => {
      window.removeEventListener('resize', refreshBoard);
    };
  }, []);

  useEffect(() => {
    const fetchTokenAndSubscribe = async () => {
      const authToken = nsToken.getDecoded();

      if (!socket) return;

      let domain = '*';
      if (board.domain && board.domain !== 'netsapiens.cloud') domain = board.domain;
      if (user && user.domain) domain = user.domain;
      const subscribeData = {
        type: 'dashboard',
        filter: board.id,
        domain,
      };

      let bearer = await nsApi.getToken();
      if (charts[0] && charts[0].data_token && charts[0].data_token.access_token) {
        bearer = charts[0].data_token.access_token;
      }

      subscribeData.bearer = bearer;

      if (authToken != null) subscribeData.user = authToken.user;

      socket.emit('subscribe', subscribeData);

      socket.off('dashboard');

      const eventTimestamp = new Date().getTime();
      const allowBoardRefreshCooldown = eventTimestamp > viewModeToggleTimestamp + 5000; // wait 5sec after switching out of edit mode

      socket.on('dashboard', (socketBoard) => {
        if (socketBoard.id === board.id && !layout.editMode && !layout.publicMode && allowBoardRefreshCooldown) {
          dispatch(changeBoard(board.id));

          dispatch(actions.showToaster({
            show: true,
            message: <FormattedMessage {...messages.BOARD_UPDATED} />,
            type: 'success',
          }));
        } else if (socketBoard.id === board.id && layout.publicMode) {
          dispatch(actions.showToaster({
            show: true,
            message: <FormattedMessage {...messages.BOARD_UPDATED} />,
            type: 'success',
          }));

          setTimeout(() => {
            // eslint-disable-next-line no-self-assign
            window.location = window.location;
          }, 3000);
        }
      });
    };

    fetchTokenAndSubscribe();
  }, [board.id]);

  useEffect(() => {
    if (!layout.editMode) {
      viewModeToggleTimestamp = new Date().getTime();
    }
  }, [layout.editMode]);

  useEffect(() => {
    /* this flag forces an extra render cycle. renderRequired supports live updating
     * when editing chart props but can be used for any out-of-cycle updates that
     * require a render cycle to draw something new
     */
    if (renderRequired) {
      dispatch(actions.renderRequired(false));
    }
  }, [renderRequired]);

  /* when dragging a grid item, the placeholder element needs to be
   * vertically offset if a Header Item is being dragged. this offset
   * is for allowing the header item to sit closer to the chart
   * elements it is meant to represent.
   * eslint-disable-next-line consistent-return
   */
  const onDragStart = (l, gridItem) => {
    const dragChartId = gridItem.i;

    const dragChart = charts.find((chart) => chart.id === dragChartId);
    if (dragChart && dragChart.type === 'Header') {
      const maxRetries = 5;
      const retryInterval = 25; // ms

      let retryCounter = 0;

      const findPlaceholderInterval = setInterval(() => {
        const placeholderElement = document.getElementsByClassName('react-grid-placeholder')[0];

        if (placeholderElement) {
          placeholderElement.classList.add('offset-placeholder');
          clearInterval(findPlaceholderInterval);
        } else if (retryCounter >= maxRetries) {
          clearInterval(findPlaceholderInterval);
        } else {
          retryCounter += 1;
        }
      }, retryInterval);
    }
  };

  const getExpandedStyle = (chartId) => {
    if (renderRequired || !chartId) { return; }

    const chartCardEl = document.getElementById(`chart_card_${chartId}`);
    if (!chartCardEl) { return; }

    const chartCardStyles = chartCardEl.style;

    /* the grid layout plugin uses transform(translate(x,y)) to control card
       * positioning, so we need to reference the chart's current transform to
       * calculate a relative position to perform the zoom behavior */
    const transformRe = /translate\(([0-9]*)px, ([0-9]*)px\)/g;
    const transformPos = transformRe.exec(chartCardStyles.transform);

    const transformHor = transformPos[1];
    const transformVer = transformPos[2];

    /* set some basic dimensions used for calculations */
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const sideSheetWidth = 311; // ballpark the sidesheet width
    const headerHeight = 56; // ballpark the header+margin vertical space
    const floatBtnsPadding = 64; // ballpark the space needed for the expandCard float btns

    const cardWidth = Number.parseInt(chartCardStyles.width, 10);
    const cardHeight = Number.parseInt(chartCardStyles.height, 10);

    /* the cardWidth may be bigger than the available work space when the
     * sidesheet is open. if it is, widthScaleRatio is derived and applied
     * as a scaling transform if it is smaller than heightScaleRatio */
    const widthScaleRatio = cardWidth > windowWidth - sideSheetWidth
      ? (windowWidth - sideSheetWidth - 40) / cardWidth : 1; // 40 pixels for some extra padding

    /* the windowHeight may be bigger than the viewport because there is
     * no vertical limit on the size of a card. if it is, heightScaleRatio
     * is derived and applied as a scaling transform if it is smaller than
     * widthScaleRatio */
    const heightScaleRatio = cardHeight > windowHeight - headerHeight - floatBtnsPadding
      ? (windowHeight - headerHeight - floatBtnsPadding - 40) / cardHeight : 1; // 40 pixels for some extra padding

    /* card scaling transform doesn't work despite my best efforts
    if (widthScaleRatio > heightScaleRatio) {
      applyScalingComplex(chartId, heightScaleRatio);
    } else if (heightScaleRatio > widthScaleRatio) {
      applyScalingComplex(chartId, widthScaleRatio);
    }
    */

    /* now we can calculate the vertical and horizontal position of a centered card */
    const positionX = ((windowWidth - sideSheetWidth) / 2) - (cardWidth / 2);
    const positionY = ((windowHeight - headerHeight - floatBtnsPadding) / 2) - (cardHeight / 2);

    /* finally, the centered card position needs to be offset by the transformed position */
    if (heightScaleRatio > widthScaleRatio) {
      // eslint-disable-next-line consistent-return
      return {
        maxWidth: windowWidth - sideSheetWidth - 80, // -80 pixels for padding
        maxHeight: cardHeight,
        marginLeft: 24 - transformHor, // 24 pixels for padding
        marginTop: positionY - transformVer,
        zIndex: 9,
      };
    } if (widthScaleRatio > heightScaleRatio) {
      // eslint-disable-next-line consistent-return
      return {
        maxWidth: cardWidth,
        maxHeight: windowHeight - headerHeight - floatBtnsPadding - 60, // -60 pixels for padding
        marginLeft: positionX - transformHor,
        marginTop: 24 - transformVer, // 24 pixels for padding
        zIndex: 9,
      };
    }
    // eslint-disable-next-line consistent-return
    return {
      maxWidth: cardWidth,
      maxHeight: cardHeight,
      marginLeft: positionX - transformHor,
      marginTop: positionY - transformVer,
      zIndex: 9,
    };
  };

  const getGridFromLayout = (chart) => {
    const boardLayout = board?.layouts?.lg;
    const defaultGrid = {
      w: 6,
      h: 4,
      x: boardLayout?.length - 1,
      y: 1,
    };

    const match = boardLayout?.find((grid) => grid.i === String(chart.id));

    const tReturn = match || defaultGrid;

    if (chart.type === 'Header') {
      // tReturn.h = 1;
      tReturn.maxH = 1;
      tReturn.minW = 2; // force minWidth prop
    } else {
      tReturn.minH = 4; // force minHeight prop
      tReturn.minW = 2; // force minWidth prop
    }

    tReturn.isResizable = true; // force resize

    return tReturn;
  };

  const getCardBase = (chart) => {
    const classes = [];
    let contentComponent;

    if (selectedChartId === chart.id) { classes.push(styles.expandCard); }

    if (chart.type === 'Header') {
      classes.push('header-card');
      classes.push(styles.headerCardContainer);
      contentComponent = <HeaderItem board={board} chart={chart} />;
    } else {
      contentComponent = <CardBase key={chart.id} board={board} chart={chart} renderRequired={chart.renderRequired} />;
    }

    return (
      <div
        id={`chart_card_${chart.id}`}
        key={chart.id}
        className={classes.join(' ')}
        data-grid={getGridFromLayout(chart)}
        style={selectedChartId === chart.id ? getExpandedStyle(chart.id) : null}
      >
        {contentComponent}
      </div>
    );
  };

  /* this is a callback provided by react-grid-layout but it
   * seems to fire a lot more than on just layout changes
   */
  const handleLayoutChange = (selectedLayout, layouts) => {
    if (layout.editMode) {
      // eslint-disable-next-line no-param-reassign
      // board.layouts = layouts;
      dispatch(actions.setBoard({
        ...board,
        layouts,
      }));
      dispatch(actions.isDirty(true));
    }
  };

  const isLiveUpdate = Boolean(selectedChartId);

  /* filter charts flagged for deletion */
  const gridItemsToRender = charts.filter((chart) => !chart.isDeleted);

  let classes = `${styles.wrapper} animate__animated animate__fadeIn`;
  if (isLiveUpdate) {
    classes += ` ${styles.noScroll}`;
  }
  if (layout.editMode) {
    // classes += ` ${styles.editing}`;
  }

  let gridPadding = 24;
  // eslint-disable-next-line no-param-reassign
  if (!board.properties) { board.properties = { card_margin: 'auto' }; } // if card_margin is not provided, use auto as default

  if (board.properties.card_margin) {
    switch (board.properties.card_margin) {
      case 'auto':
        // eslint-disable-next-line no-case-declarations
        const windowWidth = window.innerWidth;
        gridPadding = (windowWidth < 1279) ? 16 : 24;
        break;
      default:
        // eslint-disable-next-line no-restricted-globals
        if (!isNaN(board.properties.card_margin)) gridPadding = board.properties.card_margin;
        break;
    }
  }

  return (
    <div
      id="content-container"
      className={classes}
    >
      {gridItemsToRender.length > 0 && (
        <ResponsiveReactGridLayout
          breakpoints={{ lg: 0 }}
          cols={{ lg: 12 }}
          rowHeight={24}
          margin={[gridPadding, gridPadding]}
          preventCollision={false} // no automatic reposition of grid items
          layouts={board.layouts}
          draggableCancel="input,button,textarea,.header-item-action-btn"
          onDragStart={onDragStart}
          isDraggable={layout.editMode && !isLiveUpdate}
          isResizable={layout.editMode && !isLiveUpdate}
          onLayoutChange={handleLayoutChange}
        >
          {gridItemsToRender.map((chart) => getCardBase(chart))}
        </ResponsiveReactGridLayout>
      )}

      {!gridItemsToRender.length > 0 && (
        <NoChart board={board} />
      )}

      {isLiveUpdate && (
        <div className={styles.overlay} />
      )}

      {charts && charts.length > 0 && (
        <BroNoConfirm />
      )}
    </div>
  );
}

export default Dashboard;
